import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect, useMemo, useRef } from "react";
import ReactTableComponent from "../table/ReactTableComponent";
import AttribiteApis from "../apiProvider/attribute";
import apiProvider from "../apiProvider/api";
import usePermission from "../hook/usePermission";
import Swal from "sweetalert2";
import ImageCropper from "./common/ImageCropper";
import { identifyAverageColor } from "../utils/ColorIdentifier";
import { IMAGE_BASE_URL } from "../network/apiClient";

const AttributePage = () => {
  const canAdd = usePermission("attributes", "add");
  const canEdit = usePermission("attributes", "edit");
  const canDelete = usePermission("attributes", "delete");
  const havePermissions = canEdit || canDelete;
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    AttributeName: "",
    values: [{ value: "", _id: undefined, newFiles: [], images: [], preview: null }],
  });

  const [cropperState, setCropperState] = useState({ show: false, image: null, index: null });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [attributeData, setAttributeData] = useState([]);
  const [total, setTotal] = useState(0);
  const attributeFilter = ["name", "values"]
  const [pageIndex, setPageIndex] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const fetchData = async () => {
    try {
      const input = {
        page: pageIndex,
        limit: pageSize,
        // filters: filters, // Filters are not used in this component's API call structure
        search: search
      };
      console.log("Fetching data with input:", input, "Filters:", filters, "Sorting:", sorting);
      // Convert filters array to filters object
      const response = await AttribiteApis.attributeList(input);
      if (response.status) {
        setAttributeData(response.response.data || []);
        console.log(response.response.data, "response")
        setTotalPages(response.response.totalPages || 0);
        setTotal(response.response.total || 0);
      } else {
        console.error("Failed to fetch brand list");
      }
    } catch (error) {
      console.error("Error fetching brand list:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pageIndex, pageSize, filters, search]);
  const handleNextPage = () => {
    if (pageIndex + 1 < totalPages) setPageIndex(prev => prev + 1);
  };

  const handlePreviousPage = () => {
    if (pageIndex > 0) setPageIndex(prev => prev - 1);
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    // setFormData((prev) => ({
    //   ...prev,
    //   [name]: value,
    // }));
    if (name === "AttributeName") {
      // Only allow if the first character is a letter or if the input is empty
      if (value === "" || /^[a-zA-Z]/.test(value)) {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateForm = () => {
    let newErrors = {};

    if (!formData.AttributeName || !formData.AttributeName.trim()) {
      newErrors.AttributeName = "Name is required";
    }

    if (!formData.values || formData.values.length === 0) {
      newErrors.values = "At least one value is required";
    } else {
      const someEmpty = formData.values.some(v => !v.value || !v.value.trim());
      if (someEmpty) {
        newErrors.values = "Each value must have a name";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleValueChange = (index, value) => {
    const updated = [...formData.values];
    updated[index].value = value;
    setFormData(prev => ({ ...prev, values: updated }));
  };

  const addValueField = () => {
    setFormData(prev => ({
      ...prev,
      values: [...prev.values, { value: "", _id: undefined, newFiles: [], images: [], preview: null }]
    }));
  };

  const removeValueField = (index) => {
    const updated = [...formData.values];
    updated.splice(index, 1);
    setFormData(prev => ({ ...prev, values: updated }));
  };

  const handleFileChange = (e, index) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setCropperState({ show: true, image: reader.result, index });
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onCropComplete = async (cropped) => {
    const updated = [...formData.values];
    const file = new File([cropped.blob], `swatch_${cropperState.index}_${Date.now()}.jpg`, { type: "image/jpeg" });

    updated[cropperState.index].newFiles = [file];
    updated[cropperState.index].preview = cropped.url;

    // Auto-detect color name if not already set
    if (!updated[cropperState.index].value || updated[cropperState.index].value === "") {
      const color = await identifyAverageColor(cropped.url);
      if (color && color.name) {
        updated[cropperState.index].value = color.name;
      }
    }

    setFormData(prev => ({ ...prev, values: updated }));
    setCropperState({ show: false, image: null, index: null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    try {
      const resolvedValues = await Promise.all(
        formData.values.map(async (v) => {
          if (v.newFiles && v.newFiles.length > 0) {
            const uploadFormData = new FormData();
            uploadFormData.append("folder", "attributes");
            v.newFiles.forEach(file => uploadFormData.append("images", file));

            const uploadRes = await apiProvider.upload(uploadFormData);
            if (!uploadRes.status) throw new Error("Image upload failed");

            return {
              ...(v._id && { _id: v._id }),
              value: v.value,
              images: uploadRes.data
            };
          }

          return {
            ...(v._id && { _id: v._id }),
            value: v.value,
            images: v.images || []
          };
        })
      );

      const payload = {
        name: formData.AttributeName,
        value: resolvedValues
      };

      const response = isEditMode
        ? await AttribiteApis.updateattribute(payload, editId)
        : await AttribiteApis.attributecreate(payload);

      if (response.status) {
        document.querySelector("#exampleModalEdit .btn-close")?.click();
        setFormData({
          AttributeName: "",
          values: [{ value: "", _id: undefined, newFiles: [], images: [], preview: null }],
        });
        fetchData();
      }

    } catch (error) {
      Swal.fire("Error", error.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (id) => {
    const response = await AttribiteApis.attributeDetails(id);
    if (response.status) {
      const data = response.response.data;
      setIsEditMode(true);
      setEditId(id);
      setFormData({
        AttributeName: data.name,
        values: data.value
          ? data.value.map((v) => {
            const imageInfo = v.images && v.images[0] ? v.images[0] : null;
            return {
              _id: v._id,
              value: v.value,
              images: v.images || [],
              newFiles: [],
              preview: imageInfo ? `${IMAGE_BASE_URL}/${imageInfo.docPath}/${imageInfo.docName}` : null,
            };
          })
          : [{ value: "", _id: undefined, newFiles: [], images: [], preview: null }],
      });
    }
  };

  const handleRemoveRow = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const response = await AttribiteApis.deleteAttribute(id);
        if (response.status) {
          fetchData();
        }
      }
    });
  };
  const columns = useMemo(
    () => [
      {
        header: 'S.No',
        cell: info => info.row.index + 1,
      },
      {
        header: 'Attribute Name',
        accessorKey: 'name',
        cell: info => info.getValue(),
      },
      {
        accessorKey: 'values',
        header: 'Values',
        cell: info => (
          <div 
            style={{ 
              maxWidth: '400px', 
              whiteSpace: 'nowrap', 
              overflow: 'hidden', 
              textOverflow: 'ellipsis' 
            }}
            title={info.getValue()}
          >
            {info.getValue()}
          </div>
        ),
        accessorFn: row => row.value.map(e => e.value).join(', '),
      },
      // {
      //   header: 'Status',
      //   accessorKey: 'isActive',
      //   cell: info => (
      //     <span
      //       className={`${info.getValue()
      //         ? 'bg-success-focus text-success-600 border border-success-border'
      //         : 'bg-danger-focus text-danger-600 border border-danger-main'
      //         } px-24 py-4 radius-4 fw-medium text-sm`}
      //     >
      //       {info.getValue() ? 'Active' : 'Not Active'}
      //     </span>
      //   ),
      // },

      ...(havePermissions
        ? [{
          header: 'Actions',
          cell: info => {
            const attr = info.row.original;
            return (
              <div className="d-flex justify-content-start align-items-center gap-2">
                {canEdit && (
                  <button
                    type="button"
                    className="d-flex justify-content-center align-items-center bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px rounded-circle"
                    data-bs-toggle="modal"
                    data-bs-target="#exampleModalEdit"
                    onClick={() => handleEdit(attr._id)}
                  >
                    <Icon icon="lucide:edit" className="menu-icon" />
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => handleRemoveRow(attr._id)}
                    className="d-flex justify-content-center align-items-center bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px rounded-circle"
                  >
                    <Icon icon="fluent:delete-24-regular" className="menu-icon" />
                  </button>
                )}
              </div>
            );
          },
        },
        ]
        : []),
    ],
    [handleEdit, handleRemoveRow, canEdit, canDelete]
  );
  const searchDebounceRef = useRef(null);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    setPageIndex(0); // reset to first page

    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

    searchDebounceRef.current = setTimeout(() => {
      setSearch(value); // trigger useEffect after debounce
    }, 300); // 300ms debounce
  };
  return (
    <div className="card h-100 p-0 radius-12">

      {canAdd && (
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">

          <div style={{ position: "relative" }}>
            <input
              type="text"
              className="form-control"
              placeholder="Search..."
              style={{ maxWidth: 350 }}
              value={searchInput}
              onChange={handleSearchChange}
            />
            <Icon
              icon="ic:baseline-search"
              style={{
                position: "absolute",
                right: 10,
                top: 10,
                fontSize: 20,
              }}
            />
          </div>
          <div>
            <button
              type="button"
              className="btn btn-success text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-right gap-2 catalog-add-btn"
              data-bs-toggle="modal"
              data-bs-target="#exampleModalEdit"
              onClick={() => {
                setFormData({
                  AttributeName: '',
                  values: [{ value: "", _id: undefined, newFiles: [], images: [], preview: null }],
                });
                setIsEditMode(false);
              }}

            >
              <Icon
                icon="ic:baseline-plus"
                className="icon text-xl line-height-1"
              />
              Create Attribute
            </button>
          </div>
        </div>
      )}

      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table table-striped table-centered align-middle table-nowrap mb-0 table-check d-none">
            <thead>
              <tr>
                <th scope="col">S.No</th>
                <th scope="col">Attribute Name</th>
                <th scope="col">Attribute Values</th>
                <th scope="col">Status</th>
                <th scope="col" className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {attributeData.map((attr, index) => (
                <tr key={attr._id}>
                  <td>{index + 1}</td>
                  <td>
                    <span className="text-md mb-0 fw-normal text-secondary-light">
                      {attr.name}
                    </span>
                  </td>
                  <td>
                    {attr?.value?.map((e) => e.value).join(", ")}
                  </td>
                  <td>
                    <span
                      className={`${attr.isActive === true
                        ? "bg-success-focus text-success-600 border border-success-border"
                        : "bg-danger-focus text-danger-600 border border-danger-main"
                        } px-24 py-4 radius-4 fw-medium text-sm`}
                    >
                      {attr.isActive == true ? "Active" : "Not Active"}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex justify-content-center gap-2">
                      <button
                        type="button"
                        className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                        data-bs-toggle="modal"
                        data-bs-target="#exampleModalEdit"
                        onClick={() => {
                          handleEdit(attr._id);
                        }}
                      >
                        <Icon icon="lucide:edit" className="menu-icon" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveRow(attr._id)}
                        className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                      >
                        <Icon icon="fluent:delete-24-regular" className="menu-icon" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <ReactTableComponent data={attributeData}
            columns={columns}
            filterableColumns={attributeFilter}
            pageIndex={pageIndex}
            totalPages={totalPages}
            totalRecords={total}
            onNextPage={handleNextPage}
            onPreviousPage={handlePreviousPage}
            filters={filters}
            setFilters={setFilters}
          />
        </div>
        {/* Pagination buttons */}

      </div>

      {/* add About model */}

      <div
        className="modal fade"
        id="exampleModalEdit"
        tabIndex={-1}
        aria-labelledby="exampleModalEditLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content radius-16 bg-base">
            <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0">
              <h1 className="modal-title fs-5" id="exampleModalEditLabel">
                {/* <h1 className="modal-title fs-5"> */}
                {isEditMode ? "Edit Attribute" : "Create Attribute"}
                {/* </h1> */}
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body p-24">
              <form onSubmit={handleSubmit}>
                <div className="row gy-3">
                  {/* Facilitator Name */}
                  <div className="col-12">
                    <label className="form-label">Attribute Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      name="AttributeName"
                      className="form-control"
                      value={formData.AttributeName || ""}
                      onChange={handleChange}
                    />
                    {errors.AttributeName && (
                      <span className="text-danger">
                        {errors.AttributeName}
                      </span>
                    )}
                  </div>

                  {/* Attribute Values & Images */}
                  <div className="col-12">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <label className="form-label mb-0">Attribute Values & Images <span className="text-danger">*</span></label>
                      <button type="button" className="btn btn-success btn-sm radius-8 d-flex align-items-center gap-2 catalog-add-btn" onClick={addValueField}>
                        <Icon icon="ic:baseline-plus" /> Add Value
                      </button>
                    </div>

                    <div className="row gy-3">
                      {formData.values.map((val, index) => (
                        <div key={index} className="col-12 border p-2 radius-8 bg-light-50">
                          <div className="row align-items-center">
                            <div className="col-md-5">
                              <label className="text-xs fw-semibold mb-1">Value Name</label>
                              <input type="text" className="form-control form-control-sm radius-8" value={val.value} onChange={(e) => handleValueChange(index, e.target.value)} placeholder="e.g. Red" />
                            </div>
                            <div className="col-md-5">
                              <label className="text-xs fw-semibold mb-1">Swatch Image</label>
                              <div className="d-flex align-items-center gap-3">
                                <input type="file" className="form-control form-control-sm radius-8 flex-grow-1" accept="image/*" onChange={(e) => handleFileChange(e, index)} />
                                {val.preview && (
                                  <div className="border p-1 bg-white radius-4 shadow-sm">
                                    <img src={val.preview} alt="Swatch" className="radius-4" style={{ width: "32px", height: "32px", objectFit: "cover" }} />
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="col-md-2 text-end mt-4 pt-3  pe-3">
                              {formData.values.length > 1 && (
                                <button type="button" className="btn btn-danger-600 btn-sm radius-8 px-12" onClick={() => removeValueField(index)}>
                                  <Icon icon="fluent:delete-24-regular" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {errors.values && (
                      <span className="text-danger d-block mt-1">{errors.values}</span>
                    )}
                  </div>

                  <div className="modal-footer">
                    <button type="submit" className="btn btn-primary text-sm">
                      {isEditMode ? "Update" : "Submit"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* add category model */}

      {cropperState.show && (
        <ImageCropper
          image={cropperState.image}
          onCropComplete={onCropComplete}
          onCancel={() => setCropperState({ show: false, image: null, index: null })}
        />
      )}
    </div>
  );
};

export default AttributePage;
